import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, BarChart3, Instagram, Loader2, RefreshCw, Users, X } from 'lucide-react';
import {
  AccountRequest,
  getRequestArtistAnalytics,
  RequestArtistAnalytics,
} from '../../services/api/accountRequests.service';

interface Props {
  request: AccountRequest | null;
  onClose: () => void;
  onDecision: (id: number, action: 'approve' | 'reject') => Promise<boolean>;
}

const compactNumber = new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 });
const standardNumber = new Intl.NumberFormat('en');

const showNumber = (value: number | null | undefined, compact = false) =>
  value == null ? '—' : (compact ? compactNumber : standardNumber).format(value);

const showPercent = (value: number | null | undefined) => value == null ? '—' : `${value.toFixed(2)}%`;

const MetricCard: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm backdrop-blur">
    <div className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className="mt-1.5 text-2xl font-semibold tracking-tight text-slate-950">{value}</div>
    {hint && <div className="mt-1 text-[0.68rem] text-slate-500">{hint}</div>}
  </div>
);

const ProgressList: React.FC<{
  title: string;
  values: Array<{ label: string; percent: number | null; detail?: string }>;
  color: string;
}> = ({ title, values, color }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white p-4">
    <h4 className="text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-slate-600">{title}</h4>
    <div className="mt-3 space-y-3">
      {values.length === 0 ? <div className="text-xs text-slate-400">No data available</div> : values.map((item) => (
        <div key={`${item.label}-${item.detail || ''}`}>
          <div className="mb-1 flex justify-between gap-3 text-xs">
            <span className="truncate font-medium text-slate-700">{item.label}</span>
            <span className="shrink-0 text-slate-500">{item.percent == null ? '—' : `${item.percent.toFixed(1)}%`}{item.detail ? ` · ${item.detail}` : ''}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, Math.max(0, item.percent || 0))}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ArtistAnalyticsModal: React.FC<Props> = ({ request, onClose, onDecision }) => {
  const [data, setData] = useState<RequestArtistAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (!request) return;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      setData(await getRequestArtistAnalytics(request.id, { forceRefresh }));
    } catch (err: any) {
      setError(err?.message || 'Failed to load artist analytics');
      setErrorCode(err?.code || null);
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    setData(null);
    if (request) void load();
  }, [request, load]);

  useEffect(() => {
    if (!request) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', closeOnEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      document.body.style.overflow = '';
    };
  }, [request, onClose]);

  if (!request) return null;

  const decide = async (action: 'approve' | 'reject') => {
    setDecision(action);
    try {
      if (await onDecision(request.id, action)) onClose();
    } finally {
      setDecision(null);
    }
  };

  const instagramAvailable = data?.instagram.status === 'available';
  const latestSpotifyDate = data && Object.values(data.spotify.timestamps).filter(Boolean).sort().at(-1);

  return (
    <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/60 p-3 backdrop-blur-sm md:p-6" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-slate-50 shadow-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 md:px-7">
          <div>
            <div className="flex items-center gap-2 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-slate-500"><BarChart3 size={14} /> Artist intelligence</div>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">{data?.artist.name || request.artist_name || 'Artist analytics'}</h2>
            <p className="mt-0.5 text-xs text-slate-500">Decision support for {request.name || request.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => void decide('approve')} disabled={decision !== null} className="rounded-xl bg-slate-950 px-3.5 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
              {decision === 'approve' ? 'Approving…' : 'Approve'}
            </button>
            <button type="button" onClick={() => void decide('reject')} disabled={decision !== null} className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50">
              {decision === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
            {data && <button type="button" onClick={() => void load(true)} disabled={loading} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:opacity-50" title="Refresh analytics"><RefreshCw size={16} className={loading ? 'animate-spin' : ''} /></button>}
            <button type="button" onClick={onClose} className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50" aria-label="Close"><X size={18} /></button>
          </div>
        </header>

        <div className="overflow-y-auto p-4 md:p-6">
          {loading && !data ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center text-slate-500"><Loader2 className="mb-3 animate-spin" size={30} /><div className="text-sm font-medium">Loading Chartmetric analytics…</div><div className="mt-1 text-xs">Spotify and Instagram are requested securely through Nova.</div></div>
          ) : error ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center text-center" role="alert" aria-live="polite"><div className="rounded-full bg-red-50 p-3 text-red-500"><AlertCircle size={28} /></div><div className="mt-3 text-sm font-semibold text-slate-900">{errorCode === 'CHARTMETRIC_RATE_LIMITED' ? 'Chartmetric request limit reached' : 'Analytics unavailable'}</div><div className="mt-1 max-w-md text-xs leading-5 text-slate-500">{errorCode === 'CHARTMETRIC_RATE_LIMITED' ? 'Chartmetric’s request limit has been reached temporarily. Artist analytics cannot be loaded right now. Please wait a moment and try again.' : error}</div><button type="button" onClick={() => void load(true)} className="mt-4 rounded-xl bg-slate-950 px-4 py-2 text-xs font-semibold text-white">Try again</button></div>
          ) : data ? (
            <div className="grid gap-5 lg:grid-cols-2">
              <section className="overflow-hidden rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-white">
                <div className="flex items-center justify-between px-5 pt-5">
                  <div><div className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-emerald-700">Spotify</div><div className="mt-1 text-sm font-medium text-slate-700">Audience reach & conversion</div></div>
                </div>
                <div className="grid grid-cols-2 gap-3 p-5">
                  <MetricCard label="Followers" value={showNumber(data.spotify.followers, true)} hint={showNumber(data.spotify.followers)} />
                  <MetricCard label="Monthly listeners" value={showNumber(data.spotify.monthlyListeners, true)} hint={showNumber(data.spotify.monthlyListeners)} />
                  <MetricCard label="Popularity" value={data.spotify.popularity == null ? '—' : `${data.spotify.popularity}/100`} />
                  <MetricCard label="Follower conversion" value={showPercent(data.spotify.followersToListenersRatio)} hint="Followers ÷ listeners" />
                </div>
                <div className="mx-5 mb-5 rounded-2xl border border-emerald-100 bg-white/80 p-4">
                  <div className="text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-slate-500">Business signal</div>
                  <p className="mt-2 text-xs leading-5 text-slate-600">Popularity indicates current Spotify momentum. Follower conversion shows how much of the monthly audience has converted into a lasting connection.</p>
                </div>
                <div className="border-t border-emerald-100 px-5 py-3 text-[0.68rem] text-slate-500">Latest source data: {latestSpotifyDate ? new Date(latestSpotifyDate).toLocaleDateString() : 'Unavailable'}</div>
              </section>

              <section className="overflow-hidden rounded-3xl border border-fuchsia-100 bg-gradient-to-br from-fuchsia-50 via-white to-white">
                <div className="flex items-center justify-between px-5 pt-5">
                  <div><div className="flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-fuchsia-700"><Instagram size={14} /> Instagram</div><div className="mt-1 text-sm font-medium text-slate-700">Audience quality & engagement</div></div>
                </div>
                {!instagramAvailable ? (
                  <div className="flex min-h-[380px] flex-col items-center justify-center px-8 text-center"><div className="rounded-full bg-fuchsia-100 p-3 text-fuchsia-600"><Instagram size={25} /></div><div className="mt-3 text-sm font-semibold text-slate-900">Instagram analytics unavailable</div><p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">{data.instagram.reason || 'No linked Instagram audience data was returned for this artist.'}</p></div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3 p-5">
                      <MetricCard label="Followers" value={showNumber(data.instagram.followers, true)} hint={showNumber(data.instagram.followers)} />
                      <MetricCard label="Engagement rate" value={showPercent(data.instagram.engagementRate)} />
                      <MetricCard label="Avg. likes / post" value={showNumber(data.instagram.averageLikesPerPost)} />
                      <MetricCard label="Avg. comments / post" value={showNumber(data.instagram.averageCommentsPerPost)} />
                    </div>
                    <div className="grid gap-3 px-5 pb-5 sm:grid-cols-2">
                      <ProgressList title="Top countries" color="bg-fuchsia-500" values={(data.instagram.topCountries || []).map((item) => ({ label: item.name || 'Unknown', percent: item.percent, detail: item.count == null ? undefined : compactNumber.format(item.count) }))} />
                      <ProgressList title="Audience gender" color="bg-violet-500" values={(data.instagram.audienceGenders || []).map((item) => ({ label: item.label ? item.label[0].toUpperCase() + item.label.slice(1) : 'Unknown', percent: item.percent }))} />
                      <ProgressList title="Top age groups" color="bg-indigo-500" values={(data.instagram.audienceAges || []).slice().sort((a, b) => (b.totalPercent || 0) - (a.totalPercent || 0)).slice(0, 5).map((item) => ({ label: item.range || 'Unknown', percent: item.totalPercent }))} />
                      <ProgressList title="Top interests" color="bg-pink-500" values={(data.instagram.topInterests || []).map((item) => ({ label: item.name || 'Unknown', percent: item.percent }))} />
                    </div>
                    {(data.instagram.notableFollowers || []).length > 0 && <div className="mx-5 mb-5 rounded-2xl border border-fuchsia-100 bg-white p-4"><div className="mb-3 flex items-center gap-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-slate-500"><Users size={13} /> Notable followers</div><div className="flex flex-wrap gap-2">{(data.instagram.notableFollowers || []).map((follower) => <a key={follower.username || follower.fullName} href={follower.profileUrl || undefined} target="_blank" rel="noreferrer" className="rounded-full bg-slate-100 px-3 py-1.5 text-[0.7rem] font-medium text-slate-700 hover:bg-slate-200">@{follower.username || follower.fullName} · {showNumber(follower.followers, true)}</a>)}</div></div>}
                    <div className="border-t border-fuchsia-100 px-5 py-3 text-[0.68rem] text-slate-500">Latest source data: {data.instagram.timestamp ? new Date(data.instagram.timestamp).toLocaleDateString() : 'Unavailable'}</div>
                  </>
                )}
              </section>
            </div>
          ) : null}
        </div>
        {data && <footer className="border-t border-slate-200 bg-white px-5 py-3 text-right text-[0.65rem] text-slate-400">Fetched {new Date(data.fetchedAt).toLocaleString()} · Chartmetric</footer>}
      </div>
    </div>
  );
};

export default ArtistAnalyticsModal;
